/**
 * Domains Service — Step 5
 * ────────────────────────
 *
 * Thin async wrappers around the Step 3 Define Domains backend:
 *   GET  /defineDomains/{job_id}/contract
 *   GET  /defineDomains/{job_id}/proposals
 *   POST /defineDomains/save-draft
 *   POST /defineDomains/approve         (not invoked by Step 5 UI today)
 *   POST /defineDomains/re-decompose
 *
 * Plus one helper that resolves the latest define_domains job_id for an
 * (account, app) pair using the existing /jobs/by-app endpoint.
 *
 * Conventions match flowService.js: plain `fetch` + getAPIBaseUrl, errors
 * surfaced via thrown `Error` with `.status` attached when relevant.
 */

import { getAPIBaseUrl } from '../environment.js'

const JOB_TYPE = 'define_domains'


// ── Internal helpers ──────────────────────────────────────────────────────────

async function _readJsonOrThrow(response) {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = new Error(data?.detail || `HTTP ${response.status}`)
    err.status = response.status
    err.body = data
    throw err
  }
  return data
}


// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Find the latest `define_domains` job_id for this account+app, if one exists.
 * Returns null when none has been created yet.
 *
 * The `/jobs/by-app` endpoint defaults to status="completed". We try a few
 * statuses so a draft (RUNNING) job is also discoverable.
 */
export async function findDefineDomainsJobId(accountId, applicationName) {
  const base = getAPIBaseUrl()
  const statuses = ['running', 'completed', 'pending', 'failed']
  for (const status of statuses) {
    const url = `${base}/jobs/by-app/${encodeURIComponent(accountId)}/${encodeURIComponent(applicationName)}?status=${encodeURIComponent(status)}`
    let data = null
    try {
      const res = await fetch(url)
      if (!res.ok) continue
      data = await res.json()
    } catch (e) {
      void e
      continue
    }
    const jobs = data?.jobs || []
    const match = jobs.find(j => (j?.job_type || j?.type || j?.flow_type) === JOB_TYPE)
    if (match?.job_id) return match.job_id
  }
  return null
}


/**
 * GET /defineDomains/{job_id}/contract
 * Returns the persisted DomainContract (draft or finalized).
 * Throws on non-200; the caller decides how to handle 404.
 */
export async function getDomainContract(jobId) {
  const base = getAPIBaseUrl()
  const res = await fetch(`${base}/defineDomains/${encodeURIComponent(jobId)}/contract`)
  return _readJsonOrThrow(res)
}


/**
 * GET /defineDomains/{job_id}/proposals
 */
export async function getDomainProposals(jobId) {
  const base = getAPIBaseUrl()
  const res = await fetch(`${base}/defineDomains/${encodeURIComponent(jobId)}/proposals`)
  return _readJsonOrThrow(res)
}


/**
 * POST /defineDomains/save-draft
 *
 * @param {object} args
 * @param {string} args.scoutAccountId
 * @param {string} args.applicationName
 * @param {string} [args.userId]
 * @param {object} args.contract  Full DomainContract dict (draft state)
 */
export async function saveDomainDraft({ scoutAccountId, applicationName, userId, contract }) {
  const base = getAPIBaseUrl()
  const res = await fetch(`${base}/defineDomains/save-draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scout_account_id: scoutAccountId,
      application_name: applicationName,
      user_id: userId,
      contract,
    }),
  })
  return _readJsonOrThrow(res)
}


/**
 * POST /defineDomains/re-decompose
 * Generates (or regenerates) AI proposals while preserving user-pinned domains.
 */
export async function reDecomposeDomains({
  scoutAccountId,
  applicationName,
  userId,
  irPrograms,
  procedureModels,
  businessCapabilities,
  enableAi = false,
}) {
  const base = getAPIBaseUrl()
  const body = {
    scout_account_id: scoutAccountId,
    application_name: applicationName,
    user_id: userId,
    enable_ai: enableAi,
  }
  if (irPrograms) body.ir_programs = irPrograms
  if (procedureModels) body.procedure_models = procedureModels
  if (businessCapabilities) body.business_capabilities = businessCapabilities

  const res = await fetch(`${base}/defineDomains/re-decompose`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return _readJsonOrThrow(res)
}
